import { useEffect } from 'react';

import { updateAdobeTesting } from '~redux/Actions/DisplayActions';

import { useAppDispatch, useAppSelector } from './useRedux';

export default function useAdobeTarget() {
    const dispatch = useAppDispatch();

    const adobeTesting = useAppSelector((state) => state.display.adobeTesting);
    const { adobeHasLoaded, adobeContentRenderingStart, adobeTestData, adobeExperiences } =
        adobeTesting;

    const localTestingConfig = {
        personalcode: 'b',
    };

    const findAdobeTests = () => {
        // at-request-succeeded
        // An mbox request was successful
        //
        // Test naming nomenclature:
        // -----------------------------------
        // `Product Name | Environment | Test Name | JIRA ticket number`
        // e.g. MC | PRD | Hero Image AEP Test | MEDI-9999 (will be parsed as testName = 'medi-9999')
        //
        // Experience naming nomenclacure:
        // -----------------------------------
        // `ExperienceType(Experience|Redirection) ExperienceName`
        // e.g. Experience A (will be parsed as testValue = 'a' and will be trated as non-redirection experience)
        // e.g. Redirection B (will treat experience as redirection type, i.e. will not unhide preloader on AT success)
        //
        document.addEventListener('at-request-succeeded', function (e) {
            const tgtInfo = {} as Record<string, string>;
            let adobeExperiencesInfo: Array<{
                testName: string;
                testVariant: string;
                testIsRedirect: boolean;
            }> = [];
            if (
                (e as Event & { detail: { responseTokens: Record<string, string>[] } }).detail
                    .responseTokens?.length
            ) {
                (
                    e as Event & { detail: { responseTokens: Record<string, string>[] } }
                ).detail.responseTokens.forEach((r) => {
                    const testName = r['activity.name']?.split(' | ')[3]?.toLowerCase();
                    const testIsRedirect = r['experience.name']?.includes('Redirection');
                    const testValue = r['experience.name']
                        ?.replace('Experience ', '')
                        .replace('Redirection ', '')
                        .toLowerCase();
                    tgtInfo[testName] = testValue;

                    const experienceDef = {
                        testName: testName,
                        testVariant: testValue,
                        testIsRedirect: testIsRedirect,
                    };
                    adobeExperiencesInfo = [...adobeExperiencesInfo, experienceDef];
                });
            }

            // do something useful with the list we just built
            if (Object.keys(tgtInfo).length) {
                dispatch(
                    updateAdobeTesting({
                        ...adobeTesting,
                        adobeHasLoaded: true,
                        adobeTestData: tgtInfo,
                        adobeExperiences: adobeExperiencesInfo,
                    })
                );
            } else {
                updateAdobeTesting({
                    ...adobeTesting,
                    adobeHasLoaded: true,
                    adobeExperiences: adobeExperiencesInfo,
                });
            }
        });

        // at-content-rendering-start
        // This event is fired before selector polling is started and content is rendered to the page. You can use this event to track the content rendering progress.
        document.addEventListener('at-content-rendering-start', function () {
            updateAdobeTesting({
                ...adobeTesting,
                adobeContentRenderingStart: true,
            });
        });
    };

    useEffect(() => {
        // run Adobe Target effects only on client side
        if (typeof window === 'undefined') return;

        if (process.env.GATSBY_EH_ENV !== 'local') {
            if (window.location.href.includes('mboxDisable=true')) {
                // update adobeHasLoaded state variable so that default versions will appear
                dispatch(
                    updateAdobeTesting({
                        ...adobeTesting,
                        adobeHasLoaded: true,
                    })
                );
            } else {
                findAdobeTests();
            }
        } else {
            dispatch(
                updateAdobeTesting({
                    ...adobeTesting,
                    adobeHasLoaded: true,
                    adobeTestData: localTestingConfig,
                })
            );
        }
    }, []);

    return { adobeHasLoaded, adobeContentRenderingStart, adobeTestData, adobeExperiences };
}
